import { Bookmark, Calendar, Play } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { UnifiedLecture } from './lectureData';
import { getLectureSubjectTheme } from './lectureTheme';
import { LectureThumbnail } from './LectureThumbnail';

interface LectureCardProps {
  lecture: UnifiedLecture;
  onSelect: (lecture: UnifiedLecture) => void;
  isSaved?: boolean;
  onToggleSave?: (lectureUuid: string) => void;
  className?: string;
}

function getTeacherInitials(name: string): string {
  if (!name) return 'T';
  const clean = name.replace(/^(Mr\.|Ms\.|Mrs\.|Dr\.|Prof\.)\s*/i, '').trim();
  const parts = clean.split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return (clean[0] || 'T').toUpperCase();
}

export function formatLectureDate(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr.includes('T') ? dateStr : `${dateStr}T00:00:00`);
  if (isNaN(d.getTime())) return dateStr;

  const day = d.getDate();
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const weekday = days[d.getDay()];

  return `${day} ${month} ${year} · ${weekday}`;
}

export function LectureCard({
  lecture,
  onSelect,
  isSaved = false,
  onToggleSave,
  className,
}: LectureCardProps) {
  const theme = getLectureSubjectTheme(lecture.subject);
  const formattedDate = formatLectureDate(lecture.date);

  return (
    <div
      onClick={() => onSelect(lecture)}
      className={cn(
        'group flex flex-col rounded-2xl bg-slate-100/80 dark:bg-[#151928] border border-slate-200 dark:border-slate-800 hover:border-indigo-500/50 dark:hover:border-indigo-500/40  transition-all duration-200 cursor-pointer shadow-xs hover:shadow-xl hover:-translate-y-0.5 justify-between',
        className,
      )}
    >
      <div>
        {/* 1. Thumbnail Header with Play Button & Duration Overlay */}
        <div className="relative aspect-video w-full rounded-t-xl overflow-hidden bg-slate-900 mb-3 shrink-0">
          <LectureThumbnail theme={lecture.thumbnailTheme} title={lecture.title} />

          {/* Central Play Button Overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/35 transition-colors">
            <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-full bg-white/95 text-indigo-600 border border-slate-200/60 flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:bg-white transition-all duration-200">
              <Play className="h-4 w-4 ml-0.5 fill-indigo-600 text-indigo-600" />
            </div>
          </div>

          {/* Duration Badge Bottom Right */}
          {lecture.duration && (
            <div className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded-md bg-black/85 backdrop-blur-xs text-white text-[10px] font-bold tracking-wider">
              {lecture.duration}
            </div>
          )}

          {/* Optional Bookmark toggle if provided */}
          {onToggleSave && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleSave(lecture.uuid);
              }}
              className="absolute top-2 left-3 h-7 w-7 rounded-full bg-white/95 hover:bg-white text-orange-900 shadow-md border border-slate-200/60 flex items-center justify-center cursor-pointer transition-all hover:scale-105"
            >
              <Bookmark className={`h-3.5 w-3.5 ${isSaved ? 'fill-orange-900 text-orange-900' : 'text-orange-900'}`} />
            </button>
          )}
        </div>

        {/* 2. Card Content */}
        {/* Title */}
        <div className="px-3 pb-3 sm:px-3.5 sm:pb-3.5">
        <h3 className="text-sm sm:text-base font-bold text-slate-800 dark:text-slate-100 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors line-clamp-2 capitalize leading-snug">
          {lecture.title}
        </h3>

        {/* Subtitle / Description — already falls back to the topic; with neither,
            nothing is said rather than a stock line the teacher never wrote. */}
        {lecture.description && (
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 line-clamp-2 mt-1 leading-relaxed">
            {lecture.description}
          </p>
        )}

        {/* Subject Badge */}
        <div className="mt-2.5">
          <span
            className={`inline-block px-2.5 py-0.5 rounded-md text-[10px] sm:text-[11px] font-bold uppercase tracking-wider border ${theme.pill}`}
          >
            {lecture.subject}
          </span>
        </div>
       

      {/* 3. Teacher & Date Meta */}
      <div className="mt-3 flex flex-col gap-1.5">
        {/* Teacher Row */}
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-6 w-6 rounded-full bg-gradient-to-tr from-indigo-500 to-sky-500 flex items-center justify-center text-[10px] font-bold text-white shrink-0 shadow-xs ring-1 ring-white/10">
            {getTeacherInitials(lecture.teacher)}
          </div>
          <span className="text-sm text-slate-700 dark:text-slate-300 font-medium truncate">
            {lecture.teacher}
          </span>
        </div>

        {/* Date Row */}
        <div className="flex items-center gap-1.5 text-[11px] sm:text-xs text-slate-400 dark:text-slate-500 mt-0.5">
          <Calendar className="h-3.5 w-3.5 text-slate-600 dark:text-slate-500 shrink-0" />
          <span className="text-sm text-slate-600 dark:text-slate-500">
            {formattedDate}
          </span>
        </div>
      </div>
       </div>
      </div>
    </div>
  );
}
